import { FlexBox, Text } from '@dixa/lib-shared-habla';

import { UserAuditLogEntry } from 'apis/auditLogs';
import { CustomFieldAttributeValue } from 'apis/customFields';
import { CustomFieldChip } from 'components/customFields';
import { useCustomFields } from 'utils/customFields';

import { UserActivityCard } from './userActivityCard';
import { statusColors } from './userActivityTypes.utils';

type UserCustomAttributesUpdatedProps = {
  entry: UserAuditLogEntry<'USER_CUSTOM_ATTRIBUTES_UPDATED'>;
};

const mapCustomAttributeValue = (
  value: string | string[],
): CustomFieldAttributeValue =>
  Array.isArray(value)
    ? { value, data_type: 'string_list' }
    : { value, data_type: 'string' };

export const UserCustomAttributesUpdated = ({
  entry,
}: UserCustomAttributesUpdatedProps): JSX.Element => {
  const { customFields } = useCustomFields({
    entityId: 'contact',
    entityInstanceId: entry.resource.id,
    includeDeactivated: true,
    includeArchived: true,
    preferCacheValue: true,
  });

  const deletedCustomFields = customFields.filter((cf) => {
    const isDeleted = !entry.updated_attribute_values?.[cf.attribute.id];
    const wasUpdated = entry.updated_attribute_ids?.includes(cf.attribute.id);
    return isDeleted && wasUpdated;
  });

  const updatedCustomFields = customFields.filter((cf) => {
    return entry.updated_attribute_values?.[cf.attribute.id] !== undefined;
  });

  return (
    <UserActivityCard
      entry={entry}
      color={statusColors.info}
      icon="input"
      title="Custom attributes updated"
      summary={
        <FlexBox padding="100 0" gap="100" alignItems="center" flexWrap="wrap">
          {updatedCustomFields.length > 0 && (
            <>
              <Text.Paragraph variant="4">set</Text.Paragraph>
              {updatedCustomFields.map((cf) => (
                <CustomFieldChip
                  key={cf.attribute.id}
                  customField={{
                    attribute: cf.attribute,
                    value: mapCustomAttributeValue(
                      entry.updated_attribute_values[cf.attribute.id],
                    ),
                  }}
                />
              ))}
            </>
          )}
          {deletedCustomFields.length > 0 && (
            <>
              <Text.Paragraph variant="4">
                {updatedCustomFields.length > 0 ? 'and deleted' : 'deleted'}
              </Text.Paragraph>
              {deletedCustomFields.map((cf) => (
                <CustomFieldChip
                  key={cf.attribute.id}
                  customField={{
                    attribute: cf.attribute,
                    value: undefined,
                  }}
                />
              ))}
            </>
          )}
        </FlexBox>
      }
    />
  );
};
